'use client';

import { useMemo } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import { buildSocietySearchHref } from '@/lib/publicSearchPaths';
import { SEARCH_PARAM } from '@/constants/searchParams';
import { SEARCH_TYPE } from '@/constants/searchType';
import { styled } from '@mui/material';
import { Box } from '@/primitives';
import { GetSalesRow, SaleType } from '../constants';
import {
  SaleStyledCard,
  SaleCardNameLink,
  SaleCardCell,
  SaleCardPropsHeader,
  SaleCardPropsValue,
  SaleFieldGrid,
} from './sharedCardStyles';

const SaleTypeBadge = styled(Box)(({ theme }) => ({
  borderRadius: '4px',
  padding: '4px 12px',
  width: 'fit-content',
  backgroundColor: 'rgba(148, 163, 184, 0.2)',
  color: '#475569',
  fontSize: '12px',
  lineHeight: '16px',
  ...theme.applyStyles('dark', {
    color: '#cbd5e1',
  }),
}));

const saleTypeDisplayMap: Record<string, string> = {
  [SaleType.CurrentSaleListing]: 'Sales',
  [SaleType.CurrentPrivateTreatySaleListing]: 'Private Treaty',
  [SaleType.SemenListing]: 'Semen',
  [SaleType.EmbryoListing]: 'Embryo',
  [SaleType.CompletedSaleListing]: 'Sales',
  [SaleType.CompletedPrivateTreatySaleListing]: 'Private Treaty',
  [SaleType.CompletedSemenListing]: 'Semen',
  [SaleType.CompletedEmbryoListing]: 'Embryo',
};

export default function GalleryCard(props: GetSalesRow) {
  const {
    ID,
    LOT_COUNT: lotCount,
    SALE_DATE: saleDate,
    SALE_NAME: saleName,
    SALE_TYPE: saleType,
  } = props;

  const searchParams = useSearchParams();
  const routeParams = useParams<{ orgName: string }>();

  const href = useMemo(() => {
    const org = routeParams.orgName ?? '';
    if (!org) return '#';
    const currentParams = new URLSearchParams(searchParams.toString());
    currentParams.delete(SEARCH_PARAM.SEARCH_TYPE);
    currentParams.set(SEARCH_PARAM.SALE_ID, ID?.toString() ?? '');
    return buildSocietySearchHref(
      org,
      SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS,
      currentParams,
    );
  }, [searchParams, ID, routeParams.orgName]);

  const saleTypeLabel = saleTypeDisplayMap[saleType] || saleType;

  return (
    <SaleStyledCard>
      <Box>
        <Box
          sx={{
            height: '24px',
            mb: '8px',
          }}
        >
          {saleType && <SaleTypeBadge>{saleTypeLabel}</SaleTypeBadge>}
        </Box>
        <Box sx={{ minHeight: { sm: '48px' }, flex: 1, minWidth: 0 }}>
          <SaleCardNameLink href={href}>{saleName || '—'}</SaleCardNameLink>
        </Box>
      </Box>
      <SaleFieldGrid>
        <SaleCardCell>
          <SaleCardPropsHeader>Sale Date</SaleCardPropsHeader>
          <SaleCardPropsValue>{saleDate || '—'}</SaleCardPropsValue>
        </SaleCardCell>
        <SaleCardCell>
          <SaleCardPropsHeader>Lots</SaleCardPropsHeader>
          <SaleCardPropsValue>{lotCount ?? '—'}</SaleCardPropsValue>
        </SaleCardCell>
      </SaleFieldGrid>
    </SaleStyledCard>
  );
}
